import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { salesAPI } from "../../utils/salesAPI";
import { useAuth } from "../../context/AuthContext";
import { LoadingSpinner } from "../../components/Loaders";
import { EmptyState, ErrorState } from "../../components/EmptyStates";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import Pagination from "../../components/Pagination";
import {
  FiPlus,
  FiSearch,
  FiX,
  FiDownload,
  FiRefreshCw,
  FiEye,
  FiTrash2,
  FiArrowLeft,
  FiEdit2,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./Sales.css";

// ─── helpers ────────────────────────────────────────────────────────
const formatRWF = (amount) => {
  const num = parseFloat(amount || 0);
  return (
    new Intl.NumberFormat("en-RW", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num) + " RWF"
  );
};

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "CARD", label: "Card" },
  { value: "MOBILE_MONEY", label: "Mobile Money" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "CREDIT", label: "Credit" },
  { value: "OTHER", label: "Other" },
];

const getPaymentBadgeClass = (method) => {
  const map = {
    CASH: "payment-cash",
    CARD: "payment-card",
    MOBILE_MONEY: "payment-mobile_money",
    BANK_TRANSFER: "payment-bank_transfer",
    CREDIT: "payment-credit",
    OTHER: "payment-other",
  };
  return map[method] || "payment-other";
};

const EMPTY_ITEM = {
  inventory_item_id: null,
  product_name: "",
  quantity: "",
  unit_price: "",
  vat_rate: "0",
  _remaining: null, // UI-only: remaining qty from the linked inventory item
  _status: null,    // UI-only: stock status for display
};

// Stock status colours for the picker badge
const STATUS_COLOR = {
  FINISHED: "#ef4444",
  URGENT: "#f97316",
  LOW_STOCK: "#eab308",
  GIP: "#22c55e",
  DEAD: "#6b7280",
};

// ─── Component ──────────────────────────────────────────────────────
function Sales() {
  const { user } = useAuth();
  const { t } = useTranslation();

  // View state
  const [view, setView] = useState("list"); // list | create | detail | edit
  const [selectedSale, setSelectedSale] = useState(null);

  // List state
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterPayment, setFilterPayment] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const searchTimer = useRef(null);

  // Create/Edit form state
  const [formData, setFormData] = useState({
    invoice_number: "",
    customer_name: "",
    customer_tin: "",
    payment_method: "CASH",
    notes: "",
    items: [{ ...EMPTY_ITEM }],
  });
  const [formLoading, setFormLoading] = useState(false);

  // Inventory product picker state
  const [invProducts, setInvProducts] = useState([]); // full list from backend
  const [invLoading, setInvLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState({}); // { rowIdx: string }
  const [pickerOpen, setPickerOpen] = useState({}); // { rowIdx: bool }
  const pickerRefs = useRef({}); // refs to close dropdown on outside click

  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object")
      return user.organization.id;
    return user?.organization || null;
  }, [user]);

  // ─── Load inventory products for the picker ─────────────────────
  const loadInvProducts = useCallback(async () => {
    if (!effectiveOrgId) return;
    try {
      setInvLoading(true);
      const res = await salesAPI.listInventoryProducts(effectiveOrgId);
      const data = res?.data || res;
      setInvProducts(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setInvProducts([]);
    } finally {
      setInvLoading(false);
    }
  }, [effectiveOrgId]);

  // Reload inventory list whenever the create/edit form is opened so
  // remaining quantities are always up to date.
  useEffect(() => {
    if (view === "create" || view === "edit") {
      loadInvProducts();
    }
  }, [view, loadInvProducts]);

  // Close any open picker dropdown when user clicks outside
  useEffect(() => {
    const handle = (e) => {
      const stillOpen = {};
      Object.entries(pickerRefs.current).forEach(([idx, el]) => {
        if (el && el.contains(e.target)) stillOpen[idx] = true;
      });
      setPickerOpen((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => { if (stillOpen[k]) next[k] = true; });
        return next;
      });
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // Filtered suggestions for a specific row
  const getSuggestions = useCallback(
    (rowIdx) => {
      const q = (pickerSearch[rowIdx] || "").toLowerCase().trim();
      if (!q) return invProducts.slice(0, 50);
      return invProducts
        .filter((p) =>
          p.product_name.toLowerCase().includes(q) ||
          (p.batch_number || "").toLowerCase().includes(q) ||
          (p.supplier || "").toLowerCase().includes(q)
        )
        .slice(0, 50);
    },
    [invProducts, pickerSearch]
  );

  // Called when user selects a product from the dropdown
  const selectInventoryProduct = useCallback((rowIdx, product) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[rowIdx] = {
        ...items[rowIdx],
        inventory_item_id: product.id,
        product_name: product.product_name,
        unit_price: String(parseFloat(product.unit_price) || 0),
        _remaining: parseFloat(product.remaining_quantity),
        _status: product.status,
      };
      return { ...prev, items };
    });
    setPickerSearch((prev) => ({ ...prev, [rowIdx]: product.product_name }));
    setPickerOpen((prev) => ({ ...prev, [rowIdx]: false }));
  }, []);

  // ─── List data loading ──────────────────────────────────────────
  const loadSales = useCallback(async () => {
    if (!effectiveOrgId) {
      setError(t("sales.noOrganization", "No organization selected"));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: 20 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterPayment) params.payment_method = filterPayment;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
      }

      const response = await salesAPI.listSales(effectiveOrgId, params);
      const data = response?.data || response;

      if (data?.sales) {
        setSales(data.sales);
        setPagination(data.pagination);
        setSummary(data.summary);
      } else {
        setSales([]);
        setPagination(null);
        setSummary(null);
      }
      setError(null);
    } catch (err) {
      setError(err?.message || t("sales.loadFailed", "Failed to load sales"));
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [
    effectiveOrgId,
    currentPage,
    debouncedSearch,
    filterPayment,
    filterDateFrom,
    filterDateTo,
    sortBy,
    sortDir,
    t,
  ]);

  // Debounce search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    if (view === "list") loadSales();
  }, [
    effectiveOrgId,
    currentPage,
    debouncedSearch,
    filterPayment,
    filterDateFrom,
    filterDateTo,
    sortBy,
    sortDir,
    view,
    loadSales,
  ]);

  // ─── Sorting helpers ────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field)
      return (
        <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 12 }}>⇅</span>
      );
    return (
      <span style={{ marginLeft: 4, fontSize: 12 }}>
        {sortDir === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  // ─── Filter helpers ─────────────────────────────────────────────
  const activeFilterCount =
    (debouncedSearch ? 1 : 0) +
    (filterPayment ? 1 : 0) +
    (filterDateFrom ? 1 : 0) +
    (filterDateTo ? 1 : 0);

  const clearFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setFilterPayment("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSortBy("created_at");
    setSortDir("desc");
    setCurrentPage(1);
  };

  // ─── Export ─────────────────────────────────────────────────────
  const exportSales = async () => {
    if (!effectiveOrgId) return;
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterPayment) params.payment_method = filterPayment;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;

      const blob = await salesAPI.exportSales(effectiveOrgId, params);
      const url = window.URL.createObjectURL(
        blob instanceof Blob ? blob : new Blob([blob]),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "sales_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast.success(t("sales.exportSuccess", "Export downloaded"));
    } catch (err) {
      showToast.error(err?.message || t("sales.exportFailed", "Export failed"));
    }
  };

  // ─── CRUD actions ───────────────────────────────────────────────
  const handleDownloadInvoice = async (sale) => {
    if (!effectiveOrgId) return;
    try {
      const blob = await salesAPI.downloadInvoicePDF(effectiveOrgId, sale.id);
      const url = window.URL.createObjectURL(
        blob instanceof Blob ? blob : new Blob([blob]),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sale.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast.success(t("sales.invoiceDownloaded", "Invoice PDF downloaded"));
    } catch (err) {
      showToast.error(
        err?.message ||
          t("sales.invoiceDownloadFailed", "Failed to download invoice PDF"),
      );
    }
  };

  const handleViewDetail = async (sale) => {
    if (!effectiveOrgId) return;
    try {
      setLoading(true);
      const response = await salesAPI.getSale(effectiveOrgId, sale.id);
      const data = response?.data || response;
      setSelectedSale(data);
      setView("detail");
    } catch (err) {
      showToast.error(
        err?.message || t("sales.loadDetailFailed", "Failed to load sale"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sale) => {
    const confirmed = await showConfirm({
      title: t("sales.deleteSaleTitle", "Delete Sale"),
      message: t(
        "sales.deleteSaleMessage",
        `Are you sure you want to delete sale "${sale.invoice_number}"? This will also reverse the inventory impact.`,
      ),
      confirmText: t("actions.deleteUser", "Delete"),
      cancelText: t("actions.cancel", "Cancel"),
      type: "danger",
    });
    if (!confirmed) return;

    try {
      await salesAPI.deleteSale(effectiveOrgId, sale.id);
      showToast.success(t("sales.deleteSuccess", "Sale deleted successfully"));
      loadSales();
    } catch (err) {
      showToast.error(
        err?.message || t("sales.deleteFailed", "Failed to delete sale"),
      );
    }
  };

  // ─── Create / Edit form helpers ─────────────────────────────────
  const openCreateForm = async () => {
    setPickerSearch({});
    setPickerOpen({});
    try {
      const res = await salesAPI.getNextInvoice(effectiveOrgId);
      const inv = res?.data?.invoice_number || res?.invoice_number || "";
      setFormData({
        invoice_number: inv,
        customer_name: "",
        customer_tin: "",
        payment_method: "CASH",
        notes: "",
        items: [{ ...EMPTY_ITEM }],
      });
    } catch {
      setFormData({
        invoice_number: "",
        customer_name: "",
        customer_tin: "",
        payment_method: "CASH",
        notes: "",
        items: [{ ...EMPTY_ITEM }],
      });
    }
    setView("create");
  };

  const openEditForm = (sale) => {
    setSelectedSale(sale);
    const mappedItems =
      sale.items && sale.items.length > 0
        ? sale.items.map((it) => ({
            inventory_item_id: it.inventory_item_id || null,
            product_name: it.product_name,
            quantity: it.quantity,
            unit_price: it.unit_price,
            vat_rate: it.vat_rate || "0",
            _remaining: it.remaining_quantity ?? null,
            _status: null,
          }))
        : [{ ...EMPTY_ITEM }];
    // Pre-seed the search text so each row's picker input shows the product name
    const initSearch = {};
    mappedItems.forEach((it, idx) => { initSearch[idx] = it.product_name || ""; });
    setPickerSearch(initSearch);
    setPickerOpen({});
    setFormData({
      invoice_number: sale.invoice_number || "",
      customer_name: sale.customer_name || "",
      customer_tin: sale.customer_tin || "",
      payment_method: sale.payment_method || "CASH",
      notes: sale.notes || "",
      items: mappedItems,
    });
    setView("edit");
  };

  const addItemRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...EMPTY_ITEM }],
    }));
  };

  const removeItemRow = (idx) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
    // Shift picker state keys down by one after the removed index
    setPickerSearch((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const n = parseInt(k, 10);
        if (n < idx) next[k] = v;
        else if (n > idx) next[String(n - 1)] = v;
      });
      return next;
    });
    setPickerOpen((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const n = parseInt(k, 10);
        if (n < idx) next[k] = v;
        else if (n > idx) next[String(n - 1)] = v;
      });
      return next;
    });
  };

  // Clear a row's inventory link when the user manually edits the product name
  const clearInventoryLink = (idx) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], inventory_item_id: null, _remaining: null, _status: null };
      return { ...prev, items };
    });
  };

  const updateItemRow = (idx, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const computeLineTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    return qty * price;
  };

  const computeVatAmount = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    const vat = parseFloat(item.vat_rate) || 0;
    return qty * price * (vat / 100);
  };

  const computeFormTotal = () => {
    return formData.items.reduce(
      (sum, item) => sum + computeLineTotal(item),
      0,
    );
  };

  const handleSubmitSale = async () => {
    // Validation
    if (!formData.invoice_number.trim()) {
      showToast.error(t("sales.invoiceRequired", "Invoice number is required"));
      return;
    }
    const validItems = formData.items.filter(
      (it) => it.product_name && parseFloat(it.quantity) > 0,
    );
    if (validItems.length === 0) {
      showToast.error(
        t(
          "sales.itemsRequired",
          "At least one item with product name and quantity is required",
        ),
      );
      return;
    }

    const payload = {
      invoice_number: formData.invoice_number.trim(),
      customer_name: formData.customer_name.trim(),
      customer_tin: formData.customer_tin.trim(),
      payment_method: formData.payment_method,
      notes: formData.notes.trim(),
      items: validItems.map((it) => ({
        inventory_item_id: it.inventory_item_id || null,
        product_name: it.product_name.trim(),
        quantity: parseFloat(it.quantity),
        unit_price: parseFloat(it.unit_price) || 0,
        vat_rate: parseFloat(it.vat_rate) || 0,
      })),
    };

    setFormLoading(true);
    try {
      if (view === "edit" && selectedSale) {
        await salesAPI.updateSale(effectiveOrgId, selectedSale.id, payload);
        showToast.success(
          t("sales.updateSuccess", "Sale updated successfully"),
        );
      } else {
        await salesAPI.createSale(effectiveOrgId, payload);
        showToast.success(
          t("sales.createSuccess", "Sale created successfully"),
        );
      }
      setView("list");
      loadSales();
    } catch (err) {
      const msg =
        err?.response?.data?.details?.error ||
        err?.response?.data?.message ||
        err?.message ||
        t("sales.saveFailed", "Failed to save sale");
      showToast.error(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Render: Loading & Error ────────────────────────────────────
  if (loading && sales.length === 0 && view === "list") {
    return (
      <div className="page-content">
        <div className="card">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error && sales.length === 0 && view === "list") {
    return (
      <div className="page-content">
        <ErrorState error={error} onRetry={loadSales} />
      </div>
    );
  }

  // ─── Render: Detail view ────────────────────────────────────────
  if (view === "detail" && selectedSale) {
    return (
      <div className="page-content" style={{ minWidth: 0 }}>
        <div className="card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setView("list");
                setSelectedSale(null);
              }}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FiArrowLeft /> {t("common.back", "Back to List")}
            </button>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleDownloadInvoice(selectedSale)}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <FiDownload /> {t("sales.downloadInvoice", "Invoice PDF")}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => openEditForm(selectedSale)}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <FiEdit2 /> {t("common.edit", "Edit")}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleDelete(selectedSale)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  color: "#ef4444",
                }}
              >
                <FiTrash2 /> {t("common.delete", "Delete")}
              </button>
            </div>
          </div>

          <h2
            style={{
              fontSize: "20px",
              fontWeight: "600",
              margin: "0 0 20px 0",
            }}
          >
            {t("sales.saleDetail", "Sale")} #{selectedSale.invoice_number}
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-grey)",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("sales.customer", "Customer")}
              </div>
              <div style={{ fontWeight: "500" }}>
                {selectedSale.customer_name || "—"}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-grey)",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("sales.tin", "TIN")}
              </div>
              <div style={{ fontWeight: "500" }}>
                {selectedSale.customer_tin || "—"}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-grey)",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("sales.paymentMethod", "Payment")}
              </div>
              <span
                className={`payment-badge ${getPaymentBadgeClass(selectedSale.payment_method)}`}
              >
                {selectedSale.payment_method_display ||
                  selectedSale.payment_method}
              </span>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-grey)",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("sales.total", "Total")}
              </div>
              <div
                style={{
                  fontWeight: "700",
                  fontSize: "18px",
                  color: "var(--primary-blue)",
                }}
              >
                {formatRWF(selectedSale.total_amount)}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-grey)",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("sales.createdAt", "Date")}
              </div>
              <div style={{ fontWeight: "500" }}>
                {selectedSale.created_at
                  ? new Date(selectedSale.created_at).toLocaleString()
                  : "—"}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-grey)",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("sales.createdBy", "Created By")}
              </div>
              <div style={{ fontWeight: "500" }}>
                {selectedSale.created_by_email || "—"}
              </div>
            </div>
          </div>

          {selectedSale.notes && (
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-grey)",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                {t("sales.notes", "Notes")}
              </div>
              <div style={{ fontWeight: "400", whiteSpace: "pre-wrap" }}>
                {selectedSale.notes}
              </div>
            </div>
          )}

          <h3
            style={{ fontSize: "16px", fontWeight: "600", margin: "0 0 8px 0" }}
          >
            {t("sales.lineItems", "Items")}
          </h3>
          <div className="sales-table-wrapper">
            <table className="sale-detail-items">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("sales.productName", "Product")}</th>
                  <th style={{ textAlign: "right" }}>
                    {t("sales.quantity", "Qty")}
                  </th>
                  <th style={{ textAlign: "right" }}>
                    {t("sales.unitPrice", "Unit Price")}
                  </th>
                  <th style={{ textAlign: "right" }}>
                    {t("sales.vatAmount", "VAT Amount")}
                  </th>
                  <th style={{ textAlign: "right" }}>
                    {t("sales.lineTotal", "Line Total")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(selectedSale.items || []).map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td>{idx + 1}</td>
                    <td>{item.product_name}</td>
                    <td style={{ textAlign: "right" }}>{item.quantity}</td>
                    <td style={{ textAlign: "right" }}>
                      {formatRWF(item.unit_price)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatRWF(computeVatAmount(item))}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatRWF(item.line_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ textAlign: "right" }}>
                    {t("sales.grandTotal", "Grand Total")}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatRWF(selectedSale.total_amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Create / Edit form ─────────────────────────────────
  if (view === "create" || view === "edit") {
    return (
      <div className="page-content" style={{ minWidth: 0 }}>
        <div className="card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setView(selectedSale ? "detail" : "list");
              }}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FiArrowLeft /> {t("common.back", "Back")}
            </button>
            <h2 style={{ fontSize: "20px", fontWeight: "600", margin: 0 }}>
              {view === "edit"
                ? t("sales.editSale", "Edit Sale")
                : t("sales.createSale", "New Sale")}
            </h2>
            <div />
          </div>

          {/* Sale-level fields */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label>{t("sales.invoiceNumber", "Invoice Number")}</label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) =>
                  setFormData({ ...formData, invoice_number: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>{t("sales.customerName", "Customer Name")}</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) =>
                  setFormData({ ...formData, customer_name: e.target.value })
                }
                placeholder={t("sales.optional", "Optional")}
              />
            </div>
            <div className="form-group">
              <label>{t("sales.customerTin", "Customer TIN")}</label>
              <input
                type="text"
                value={formData.customer_tin}
                onChange={(e) =>
                  setFormData({ ...formData, customer_tin: e.target.value })
                }
                placeholder={t("sales.optional", "Optional")}
              />
            </div>
            <div className="form-group">
              <label>{t("sales.paymentMethod", "Payment Method")}</label>
              <select
                value={formData.payment_method}
                onChange={(e) =>
                  setFormData({ ...formData, payment_method: e.target.value })
                }
              >
                {PAYMENT_METHODS.map((pm) => (
                  <option key={pm.value} value={pm.value}>
                    {pm.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>{t("sales.notes", "Notes")}</label>
              <textarea
                rows={2}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder={t("sales.notesPlaceholder", "Optional notes...")}
              />
            </div>
          </div>

          {/* Line items */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <h3 style={{ fontSize: "16px", fontWeight: "600", margin: 0 }}>
              {t("sales.lineItems", "Items")}
              {invLoading && (
                <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-grey)", fontWeight: 400 }}>
                  {t("sales.loadingProducts", "Loading inventory…")}
                </span>
              )}
            </h3>
            <button
              className="btn btn-secondary btn-sm"
              onClick={addItemRow}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FiPlus /> {t("sales.addRow", "Add Row")}
            </button>
          </div>

          {invProducts.length === 0 && !invLoading && (
            <div style={{
              padding: "10px 14px",
              marginBottom: "12px",
              borderRadius: "8px",
              background: "#fef9c3",
              border: "1px solid #fde68a",
              fontSize: "13px",
              color: "#92400e",
            }}>
              {t("sales.noInventoryHint",
                "No inventory items found. You can still type product names manually, but they won't deduct from stock. Add items to Inventory first for accurate tracking.")}
            </div>
          )}

          <div className="sales-table-wrapper">
            <table className="sale-items-table">
              <thead>
                <tr>
                  <th style={{ width: "32%" }}>
                    {t("sales.productName", "Product")}
                  </th>
                  <th style={{ width: "12%" }}>{t("sales.quantity", "Qty")}</th>
                  <th style={{ width: "15%" }}>
                    {t("sales.unitPrice", "Unit Price")}
                  </th>
                  <th style={{ width: "10%" }}>
                    {t("sales.vatRate", "VAT %")}
                  </th>
                  <th style={{ width: "16%", textAlign: "right" }}>
                    {t("sales.lineTotal", "Line Total")}
                  </th>
                  <th style={{ width: "5%" }} />
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, idx) => {
                  const suggestions = getSuggestions(idx);
                  const isOpen = !!pickerOpen[idx];
                  const remaining = item._remaining;
                  const statusColor = item._status ? STATUS_COLOR[item._status] : null;
                  const qty = parseFloat(item.quantity) || 0;
                  const overStock = remaining !== null && qty > remaining;

                  return (
                  <tr key={idx}>
                    {/* ── Product picker cell ── */}
                    <td style={{ position: "relative" }}
                        ref={(el) => { pickerRefs.current[idx] = el; }}>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          value={pickerSearch[idx] ?? item.product_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPickerSearch((p) => ({ ...p, [idx]: val }));
                            updateItemRow(idx, "product_name", val);
                            clearInventoryLink(idx);
                            setPickerOpen((p) => ({ ...p, [idx]: true }));
                          }}
                          onFocus={() =>
                            setPickerOpen((p) => ({ ...p, [idx]: true }))
                          }
                          placeholder={t("sales.productPlaceholder", "Search or type product…")}
                          style={{ paddingRight: item.inventory_item_id ? "26px" : undefined }}
                        />
                        {/* Linked badge */}
                        {item.inventory_item_id && (
                          <span title="Linked to inventory" style={{
                            position: "absolute", right: 6, top: "50%",
                            transform: "translateY(-50%)",
                            width: 8, height: 8, borderRadius: "50%",
                            background: statusColor || "#22c55e",
                            display: "inline-block",
                          }} />
                        )}
                      </div>

                      {/* Stock hint below input */}
                      {item.inventory_item_id && remaining !== null && (
                        <div style={{
                          fontSize: 11,
                          marginTop: 2,
                          color: remaining <= 0 ? "#ef4444"
                            : remaining <= 10 ? "#f97316"
                            : "#6b7280",
                        }}>
                          {remaining <= 0
                            ? t("sales.outOfStock", "Out of stock")
                            : t("sales.inStock", `${remaining} in stock`)}
                        </div>
                      )}

                      {/* Dropdown */}
                      {isOpen && (
                        <div style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          zIndex: 999,
                          background: "var(--card-bg, #fff)",
                          border: "1px solid var(--border-grey, #e5e7eb)",
                          borderRadius: 8,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          maxHeight: 260,
                          overflowY: "auto",
                        }}>
                          {suggestions.length === 0 ? (
                            <div style={{ padding: "10px 14px", color: "var(--text-grey)", fontSize: 13 }}>
                              {invLoading
                                ? t("sales.loadingProducts", "Loading…")
                                : t("sales.noProductsFound", "No products found")}
                            </div>
                          ) : (
                            suggestions.map((p) => {
                              const rem = parseFloat(p.remaining_quantity);
                              const isFinished = p.status === "FINISHED" || rem <= 0;
                              return (
                                <div
                                  key={p.id}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    if (!isFinished) selectInventoryProduct(idx, p);
                                  }}
                                  style={{
                                    padding: "8px 14px",
                                    cursor: isFinished ? "not-allowed" : "pointer",
                                    opacity: isFinished ? 0.45 : 1,
                                    borderBottom: "1px solid var(--border-grey, #f3f4f6)",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 8,
                                    background:
                                      item.inventory_item_id === p.id
                                        ? "var(--primary-blue-light, #eff6ff)"
                                        : undefined,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isFinished) e.currentTarget.style.background = "var(--hover-bg, #f9fafb)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                      item.inventory_item_id === p.id
                                        ? "var(--primary-blue-light, #eff6ff)"
                                        : "";
                                  }}
                                >
                                  <div>
                                    <div style={{ fontWeight: 500, fontSize: 13 }}>{p.product_name}</div>
                                    {p.batch_number && (
                                      <div style={{ fontSize: 11, color: "var(--text-grey)" }}>
                                        Batch: {p.batch_number}
                                        {p.supplier ? ` · ${p.supplier}` : ""}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600,
                                      color: STATUS_COLOR[p.status] || "#6b7280" }}>
                                      {p.status}
                                    </div>
                                    <div style={{ fontSize: 11, color: "var(--text-grey)" }}>
                                      {rem <= 0 ? "0" : rem} left
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </td>

                    {/* ── Qty cell ── */}
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={item.quantity}
                        max={remaining !== null ? remaining : undefined}
                        onChange={(e) =>
                          updateItemRow(idx, "quantity", e.target.value)
                        }
                        style={overStock ? { borderColor: "#ef4444" } : {}}
                      />
                      {overStock && (
                        <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>
                          {t("sales.exceedsStock", `Max: ${remaining}`)}
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) =>
                          updateItemRow(idx, "unit_price", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={item.vat_rate}
                        onChange={(e) =>
                          updateItemRow(idx, "vat_rate", e.target.value)
                        }
                      />
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "600" }}>
                      {formatRWF(computeLineTotal(item))}
                    </td>
                    <td>
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItemRow(idx)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#ef4444",
                            padding: "4px",
                          }}
                          title={t("sales.removeRow", "Remove")}
                        >
                          <FiX />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td
                    colSpan={4}
                    style={{ textAlign: "right", fontWeight: "700" }}
                  >
                    {t("sales.grandTotal", "Grand Total")}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: "700",
                      fontSize: "16px",
                      color: "var(--primary-blue)",
                    }}
                  >
                    {formatRWF(computeFormTotal())}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Submit */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <button
              className="btn btn-secondary"
              onClick={() => {
                setView(selectedSale ? "detail" : "list");
              }}
              disabled={formLoading}
            >
              {t("actions.cancel", "Cancel")}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmitSale}
              disabled={formLoading}
            >
              {formLoading
                ? t("common.saving", "Saving...")
                : view === "edit"
                  ? t("sales.updateSale", "Update Sale")
                  : t("sales.saveSale", "Save Sale")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: List view (default) ───────────────────────────────
  return (
    <div className="page-content" style={{ minWidth: 0 }}>
      {/* Filters & Search */}
      <div className="card" style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "600", margin: 0 }}>
            {t("sales.filtersTitle", "Filters & Search")}
            {activeFilterCount > 0 && (
              <span
                style={{
                  marginLeft: "10px",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  backgroundColor: "var(--primary-blue, #4f46e5)",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                {activeFilterCount} active
              </span>
            )}
          </h2>
          {activeFilterCount > 0 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={clearFilters}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FiX /> {t("sales.clearFilters", "Clear Filters")}
            </button>
          )}
        </div>
        <div className="sales-filters">
          {/* Search */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("sales.searchLabel", "Search Sales")}
            </label>
            <div style={{ position: "relative" }}>
              <FiSearch
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-grey)",
                }}
              />
              <input
                type="text"
                placeholder={t(
                  "sales.searchPlaceholder",
                  "Invoice, customer, TIN...",
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: "10px 12px 10px 40px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-grey)",
                  width: "100%",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          {/* Payment method */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("sales.paymentFilter", "Payment Method")}
            </label>
            <select
              value={filterPayment}
              onChange={(e) => {
                setFilterPayment(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-grey)",
                width: "100%",
                fontSize: "14px",
              }}
            >
              <option value="">{t("sales.allPayments", "All Payments")}</option>
              {PAYMENT_METHODS.map((pm) => (
                <option key={pm.value} value={pm.value}>
                  {pm.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date from */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("sales.dateFrom", "Date From")}
            </label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => {
                setFilterDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-grey)",
                width: "100%",
                fontSize: "14px",
              }}
            />
          </div>

          {/* Date to */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("sales.dateTo", "Date To")}
            </label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => {
                setFilterDateTo(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-grey)",
                width: "100%",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            marginTop: "16px",
          }}
        >
          <button
            className="btn btn-primary btn-sm"
            onClick={openCreateForm}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiPlus /> {t("sales.newSale", "New Sale")}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={exportSales}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiDownload /> {t("sales.exportCsv", "Export CSV")}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={loadSales}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiRefreshCw /> {t("common.refresh", "Refresh")}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="sales-stats">
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "var(--primary-blue)",
              }}
            >
              {formatRWF(summary.total_revenue || 0)}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("sales.totalRevenue", "Total Revenue")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#10b981" }}
            >
              {summary.total_sales || 0}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("sales.totalSales", "Total Sales")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#3b82f6" }}
            >
              {formatRWF(summary.today_revenue || 0)}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("sales.todayRevenue", "Today's Revenue")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#a855f7" }}
            >
              {summary.today_count || 0}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("sales.todaySales", "Today's Sales")}
            </div>
          </div>
        </div>
      )}

      {/* Sales Table */}
      <div className="card" style={{ minWidth: 0 }}>
        <div className="sales-table-wrapper">
          {sales.length === 0 ? (
            debouncedSearch ||
            filterPayment ||
            filterDateFrom ||
            filterDateTo ? (
              <EmptyState
                icon={<FiSearch />}
                title={t("sales.noSearchResults", "No results found")}
                message={t(
                  "sales.noSearchResultsMessage",
                  `No sales matching "${debouncedSearch || ""}" were found. Try a different search term or clear filters.`,
                )}
                action={
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterPayment("");
                      setFilterDateFrom("");
                      setFilterDateTo("");
                    }}
                  >
                    {t("common.clearFilters", "Clear Filters")}
                  </button>
                }
              />
            ) : (
              <EmptyState
                icon={<FiPlus />}
                title={t("sales.noSales", "No sales yet")}
                message={t(
                  "sales.noSalesMessage",
                  "Create your first sale to get started.",
                )}
                action={
                  <button className="btn btn-primary" onClick={openCreateForm}>
                    {t("sales.newSale", "New Sale")}
                  </button>
                }
              />
            )
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("invoice_number")}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {t("sales.invoice", "Invoice")}{" "}
                    {renderSortIcon("invoice_number")}
                  </th>
                  <th
                    onClick={() => handleSort("customer_name")}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {t("sales.customer", "Customer")}{" "}
                    {renderSortIcon("customer_name")}
                  </th>
                  <th
                    onClick={() => handleSort("payment_method")}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {t("sales.payment", "Payment")}{" "}
                    {renderSortIcon("payment_method")}
                  </th>
                  <th>{t("sales.products", "Products")}</th>
                  <th style={{ textAlign: "center" }}>
                    {t("sales.items", "Items")}
                  </th>
                  <th
                    onClick={() => handleSort("total_amount")}
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      textAlign: "right",
                    }}
                  >
                    {t("sales.total", "Total")} {renderSortIcon("total_amount")}
                  </th>
                  <th
                    onClick={() => handleSort("created_at")}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    {t("sales.date", "Date")} {renderSortIcon("created_at")}
                  </th>
                  <th style={{ textAlign: "center" }}>
                    {t("common.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td style={{ fontWeight: "600" }}>{sale.invoice_number}</td>
                    <td>{sale.customer_name || "—"}</td>
                    <td>
                      <span
                        className={`payment-badge ${getPaymentBadgeClass(sale.payment_method)}`}
                      >
                        {sale.payment_method_display || sale.payment_method}
                      </span>
                    </td>
                    <td
                      style={{
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={sale.product_names || "—"}
                    >
                      {sale.product_names || "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {sale.items_count || 0}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "600" }}>
                      {formatRWF(sale.total_amount)}
                    </td>
                    <td>
                      {sale.created_at
                        ? new Date(sale.created_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleViewDetail(sale)}
                          title={t("common.view", "View")}
                          style={{ padding: "6px 10px" }}
                        >
                          <FiEye />
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleDownloadInvoice(sale)}
                          title={t("sales.downloadInvoice", "Invoice PDF")}
                          style={{ padding: "6px 10px" }}
                        >
                          <FiDownload />
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleDelete(sale)}
                          title={t("common.delete", "Delete")}
                          style={{ padding: "6px 10px", color: "#ef4444" }}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <Pagination pagination={pagination} onPageChange={setCurrentPage} />
        )}
      </div>
    </div>
  );
}

export default Sales;
